/**
 * SEO landing routes: dedicated URLs with article + FAQ blocks for search / AI discovery.
 * Firebase Hosting SPA rewrite sends all paths to index.html; App reads pathname.
 */

import { toLogicalPathname } from './localeSeoRoutes'

export const SEO_LANDING_PATHS = new Set([
  '/esignature',
  '/esignature/real-estate',
  '/esignature/accountants',
  '/esignature/hr',
  '/esignature/agencies',
  '/security',
  '/esignature-legal',
  '/about',
  '/terms',
  '/privacy',
  '/docusign-alternative',
  '/docusign-vs-prime-document-sign',
  '/free-esignature',
  '/templates',
  '/templates/nda',
  '/templates/contract',
  '/templates/freelance-agreement',
  '/templates/lease-agreement',
  '/templates/service-agreement',
  '/templates/independent-contractor',
  '/templates/purchase-order',
  '/templates/photo-release',
  '/esignature-australia',
  '/why-primedocumentsign',
  '/trust',
  '/product',
  '/alternatives',
  '/what-is-esignature',
  '/reviews',
])

/** Parent crumb for industry pages under /esignature/… */
const ESIGNATURE_HUB_BREADCRUMB = {
  labelKey: 'seoLanding.esignature.breadcrumb',
  hrefLogical: '/esignature',
}

const TEMPLATES_HUB_BREADCRUMB = {
  labelKey: 'seoLanding.templates.breadcrumb',
  hrefLogical: '/templates',
}

export function normalizePathname(pathname) {
  if (!pathname || typeof pathname !== 'string') return '/'
  let p = pathname.trim()
  if (p.length > 1 && p.endsWith('/')) p = p.slice(0, -1)
  return p || '/'
}

export function isSeoLandingPathname(pathname) {
  return SEO_LANDING_PATHS.has(normalizePathname(pathname))
}

/** @returns {{ mode: 'list' } | { mode: 'post', slug: string } | null} */
export function parseBlogPath(pathname) {
  const p = normalizePathname(toLogicalPathname(pathname))
  if (p === '/blog') return { mode: 'list' }
  if (p.startsWith('/blog/')) {
    const slug = p.slice(6)
    if (slug && !slug.includes('/')) return { mode: 'post', slug }
  }
  return null
}

/** i18n keys + DOM id for each SEO article (except shared labels). */
export const SEO_ARTICLE_PAGE_KEYS = {
  '/esignature': {
    htmlId: 'esignature-top',
    breadcrumbKey: 'seoLanding.esignature.breadcrumb',
    titleKey: 'seoLanding.esignature.title',
    leadKey: 'seoLanding.esignature.lead',
    bodyKeys: ['seoLanding.esignature.p1', 'seoLanding.esignature.p2', 'seoLanding.esignature.p3'],
    faqHeadingKey: 'seoLanding.esignature.faqHeading',
    ctaKey: 'seoLanding.esignature.cta',
    pricingHref: '#pricing',
    pricingLabelKey: 'seoLanding.esignature.pricingLink',
    crossLinks: [
      { hrefLogical: '/what-is-esignature', labelKey: 'footer.linkWhatIsEsign' },
      { hrefLogical: '/product', labelKey: 'footer.linkProduct' },
      { hrefLogical: '/trust', labelKey: 'footer.linkTrust' },
      { hrefLogical: '/docusign-alternative', labelKey: 'footer.docusignAlt' },
    ],
  },
  '/esignature/real-estate': {
    htmlId: 'esignature-real-estate-top',
    breadcrumbPredecessor: ESIGNATURE_HUB_BREADCRUMB,
    breadcrumbKey: 'seoLanding.esignRe.breadcrumb',
    titleKey: 'seoLanding.esignRe.title',
    leadKey: 'seoLanding.esignRe.lead',
    bodyKeys: ['seoLanding.esignRe.p1', 'seoLanding.esignRe.p2', 'seoLanding.esignRe.p3'],
    faqHeadingKey: 'seoLanding.esignature.faqHeading',
    ctaKey: 'seoLanding.esignature.cta',
    pricingHref: '#pricing',
    pricingLabelKey: 'seoLanding.esignature.pricingLink',
  },
  '/esignature/accountants': {
    htmlId: 'esignature-accountants-top',
    breadcrumbPredecessor: ESIGNATURE_HUB_BREADCRUMB,
    breadcrumbKey: 'seoLanding.esignAcct.breadcrumb',
    titleKey: 'seoLanding.esignAcct.title',
    leadKey: 'seoLanding.esignAcct.lead',
    bodyKeys: ['seoLanding.esignAcct.p1', 'seoLanding.esignAcct.p2', 'seoLanding.esignAcct.p3'],
    faqHeadingKey: 'seoLanding.esignature.faqHeading',
    ctaKey: 'seoLanding.esignature.cta',
    pricingHref: '#pricing',
    pricingLabelKey: 'seoLanding.esignature.pricingLink',
  },
  '/esignature/hr': {
    htmlId: 'esignature-hr-top',
    breadcrumbPredecessor: ESIGNATURE_HUB_BREADCRUMB,
    breadcrumbKey: 'seoLanding.esignHr.breadcrumb',
    titleKey: 'seoLanding.esignHr.title',
    leadKey: 'seoLanding.esignHr.lead',
    bodyKeys: ['seoLanding.esignHr.p1', 'seoLanding.esignHr.p2', 'seoLanding.esignHr.p3'],
    faqHeadingKey: 'seoLanding.esignature.faqHeading',
    ctaKey: 'seoLanding.esignature.cta',
    pricingHref: '#pricing',
    pricingLabelKey: 'seoLanding.esignature.pricingLink',
  },
  '/esignature/agencies': {
    htmlId: 'esignature-agencies-top',
    breadcrumbPredecessor: ESIGNATURE_HUB_BREADCRUMB,
    breadcrumbKey: 'seoLanding.esignAgency.breadcrumb',
    titleKey: 'seoLanding.esignAgency.title',
    leadKey: 'seoLanding.esignAgency.lead',
    bodyKeys: ['seoLanding.esignAgency.p1', 'seoLanding.esignAgency.p2', 'seoLanding.esignAgency.p3'],
    faqHeadingKey: 'seoLanding.esignature.faqHeading',
    ctaKey: 'seoLanding.esignature.cta',
    pricingHref: '#pricing',
    pricingLabelKey: 'seoLanding.esignature.pricingLink',
  },
  '/docusign-alternative': {
    htmlId: 'docusign-alternative-top',
    breadcrumbKey: 'docusignAlt.breadcrumb',
    titleKey: 'docusignAlt.title',
    leadKey: 'docusignAlt.lead',
    bodyKeys: ['docusignAlt.p1', 'docusignAlt.p2', 'docusignAlt.p3'],
    faqHeadingKey: 'docusignAlt.faqHeading',
    ctaKey: 'docusignAlt.cta',
    pricingHref: '#pricing',
    pricingLabelKey: 'docusignAlt.pricingLink',
    showCompareTableAfterBody: true,
    crossLinks: [
      { hrefLogical: '/alternatives', labelKey: 'footer.linkAlternatives' },
      { hrefLogical: '/product', labelKey: 'footer.linkProduct' },
      { hrefLogical: '/trust', labelKey: 'footer.linkTrust' },
      { hrefLogical: '/esignature', labelKey: 'footer.linkEsignature' },
    ],
  },
  '/docusign-vs-prime-document-sign': {
    htmlId: 'docusign-vs-prime-top',
    breadcrumbKey: 'seoLanding.docuVs.breadcrumb',
    titleKey: 'seoLanding.docuVs.title',
    bodyKeys: [],
    faqHeadingKey: 'seoLanding.docuVs.faqHeading',
    ctaKey: 'docusignAlt.cta',
    pricingHref: '#pricing',
    pricingLabelKey: 'docusignAlt.pricingLink',
    showCompareTableAfterBody: true,
    crossLinks: [
      { hrefLogical: '/alternatives', labelKey: 'footer.linkAlternatives' },
      { hrefLogical: '/docusign-alternative', labelKey: 'footer.docusignAlt' },
      { hrefLogical: '/product', labelKey: 'footer.linkProduct' },
      { hrefLogical: '/trust', labelKey: 'footer.linkTrust' },
    ],
  },
  '/free-esignature': {
    htmlId: 'free-esignature-top',
    breadcrumbKey: 'seoLanding.freeEsign.breadcrumb',
    titleKey: 'seoLanding.freeEsign.title',
    leadKey: 'seoLanding.freeEsign.lead',
    bodyKeys: ['seoLanding.freeEsign.p1', 'seoLanding.freeEsign.p2', 'seoLanding.freeEsign.p3'],
    faqHeadingKey: 'seoLanding.freeEsign.faqHeading',
    ctaKey: 'seoLanding.freeEsign.cta',
    pricingHref: '#pricing',
    pricingLabelKey: 'seoLanding.freeEsign.pricingLink',
  },
  '/templates': {
    htmlId: 'templates-top',
    breadcrumbKey: 'seoLanding.templates.breadcrumb',
    titleKey: 'seoLanding.templates.title',
    leadKey: 'seoLanding.templates.lead',
    bodyKeys: ['seoLanding.templates.p1', 'seoLanding.templates.p2', 'seoLanding.templates.p3'],
    faqHeadingKey: 'seoLanding.templates.faqHeading',
    ctaKey: 'seoLanding.templates.cta',
    pricingHref: '#pricing',
    pricingLabelKey: 'seoLanding.templates.pricingLink',
    crossLinks: [
      { hrefLogical: '/templates/nda', labelKey: 'footer.linkTplNda' },
      { hrefLogical: '/templates/contract', labelKey: 'footer.linkTplContract' },
      { hrefLogical: '/templates/freelance-agreement', labelKey: 'footer.linkTplFreelance' },
      { hrefLogical: '/templates/lease-agreement', labelKey: 'footer.linkTplLease' },
      { hrefLogical: '/templates/service-agreement', labelKey: 'footer.linkTplService' },
      { hrefLogical: '/templates/independent-contractor', labelKey: 'footer.linkTplContractor' },
      { hrefLogical: '/templates/purchase-order', labelKey: 'footer.linkTplPurchaseOrder' },
      { hrefLogical: '/templates/photo-release', labelKey: 'footer.linkTplPhotoRelease' },
    ],
  },
  '/templates/nda': {
    htmlId: 'templates-nda-top',
    breadcrumbPredecessor: TEMPLATES_HUB_BREADCRUMB,
    breadcrumbKey: 'seoLanding.tplNda.breadcrumb',
    titleKey: 'seoLanding.tplNda.title',
    leadKey: 'seoLanding.tplNda.lead',
    bodyKeys: ['seoLanding.tplNda.p1', 'seoLanding.tplNda.p2', 'seoLanding.tplNda.p3'],
    faqHeadingKey: 'seoLanding.templates.faqHeading',
    ctaKey: 'seoLanding.templates.cta',
    pricingHref: '#pricing',
    pricingLabelKey: 'seoLanding.templates.pricingLink',
  },
  '/templates/contract': {
    htmlId: 'templates-contract-top',
    breadcrumbPredecessor: TEMPLATES_HUB_BREADCRUMB,
    breadcrumbKey: 'seoLanding.tplContract.breadcrumb',
    titleKey: 'seoLanding.tplContract.title',
    leadKey: 'seoLanding.tplContract.lead',
    bodyKeys: ['seoLanding.tplContract.p1', 'seoLanding.tplContract.p2', 'seoLanding.tplContract.p3'],
    faqHeadingKey: 'seoLanding.templates.faqHeading',
    ctaKey: 'seoLanding.templates.cta',
    pricingHref: '#pricing',
    pricingLabelKey: 'seoLanding.templates.pricingLink',
  },
  '/templates/freelance-agreement': {
    htmlId: 'templates-freelance-top',
    breadcrumbPredecessor: TEMPLATES_HUB_BREADCRUMB,
    breadcrumbKey: 'seoLanding.tplFreelance.breadcrumb',
    titleKey: 'seoLanding.tplFreelance.title',
    leadKey: 'seoLanding.tplFreelance.lead',
    bodyKeys: ['seoLanding.tplFreelance.p1', 'seoLanding.tplFreelance.p2', 'seoLanding.tplFreelance.p3'],
    faqHeadingKey: 'seoLanding.templates.faqHeading',
    ctaKey: 'seoLanding.templates.cta',
    pricingHref: '#pricing',
    pricingLabelKey: 'seoLanding.templates.pricingLink',
  },
  '/templates/lease-agreement': {
    htmlId: 'templates-lease-top',
    breadcrumbPredecessor: TEMPLATES_HUB_BREADCRUMB,
    breadcrumbKey: 'seoLanding.tplLease.breadcrumb',
    titleKey: 'seoLanding.tplLease.title',
    leadKey: 'seoLanding.tplLease.lead',
    bodyKeys: ['seoLanding.tplLease.p1', 'seoLanding.tplLease.p2', 'seoLanding.tplLease.p3'],
    faqHeadingKey: 'seoLanding.templates.faqHeading',
    ctaKey: 'seoLanding.templates.cta',
    pricingHref: '#pricing',
    pricingLabelKey: 'seoLanding.templates.pricingLink',
  },
  '/templates/service-agreement': {
    htmlId: 'templates-service-top',
    breadcrumbPredecessor: TEMPLATES_HUB_BREADCRUMB,
    breadcrumbKey: 'seoLanding.tplService.breadcrumb',
    titleKey: 'seoLanding.tplService.title',
    leadKey: 'seoLanding.tplService.lead',
    bodyKeys: ['seoLanding.tplService.p1', 'seoLanding.tplService.p2', 'seoLanding.tplService.p3'],
    faqHeadingKey: 'seoLanding.templates.faqHeading',
    ctaKey: 'seoLanding.templates.cta',
    pricingHref: '#pricing',
    pricingLabelKey: 'seoLanding.templates.pricingLink',
  },
  '/templates/independent-contractor': {
    htmlId: 'templates-contractor-top',
    breadcrumbPredecessor: TEMPLATES_HUB_BREADCRUMB,
    breadcrumbKey: 'seoLanding.tplContractor.breadcrumb',
    titleKey: 'seoLanding.tplContractor.title',
    leadKey: 'seoLanding.tplContractor.lead',
    bodyKeys: ['seoLanding.tplContractor.p1', 'seoLanding.tplContractor.p2', 'seoLanding.tplContractor.p3'],
    faqHeadingKey: 'seoLanding.templates.faqHeading',
    ctaKey: 'seoLanding.templates.cta',
    pricingHref: '#pricing',
    pricingLabelKey: 'seoLanding.templates.pricingLink',
  },
  '/templates/purchase-order': {
    htmlId: 'templates-po-top',
    breadcrumbPredecessor: TEMPLATES_HUB_BREADCRUMB,
    breadcrumbKey: 'seoLanding.tplPo.breadcrumb',
    titleKey: 'seoLanding.tplPo.title',
    leadKey: 'seoLanding.tplPo.lead',
    bodyKeys: ['seoLanding.tplPo.p1', 'seoLanding.tplPo.p2', 'seoLanding.tplPo.p3'],
    faqHeadingKey: 'seoLanding.templates.faqHeading',
    ctaKey: 'seoLanding.templates.cta',
    pricingHref: '#pricing',
    pricingLabelKey: 'seoLanding.templates.pricingLink',
  },
  '/templates/photo-release': {
    htmlId: 'templates-photo-release-top',
    breadcrumbPredecessor: TEMPLATES_HUB_BREADCRUMB,
    breadcrumbKey: 'seoLanding.tplPhotoRelease.breadcrumb',
    titleKey: 'seoLanding.tplPhotoRelease.title',
    leadKey: 'seoLanding.tplPhotoRelease.lead',
    bodyKeys: ['seoLanding.tplPhotoRelease.p1', 'seoLanding.tplPhotoRelease.p2', 'seoLanding.tplPhotoRelease.p3'],
    faqHeadingKey: 'seoLanding.templates.faqHeading',
    ctaKey: 'seoLanding.templates.cta',
    pricingHref: '#pricing',
    pricingLabelKey: 'seoLanding.templates.pricingLink',
  },
  '/esignature-australia': {
    htmlId: 'esignature-australia-top',
    breadcrumbKey: 'seoLanding.esignAu.breadcrumb',
    titleKey: 'seoLanding.esignAu.title',
    leadKey: 'seoLanding.esignAu.lead',
    bodyKeys: ['seoLanding.esignAu.p1', 'seoLanding.esignAu.p2', 'seoLanding.esignAu.p3'],
    faqHeadingKey: 'seoLanding.esignAu.faqHeading',
    ctaKey: 'seoLanding.esignAu.cta',
    pricingHref: '#pricing',
    pricingLabelKey: 'seoLanding.esignAu.pricingLink',
  },
  '/security': {
    htmlId: 'security-top',
    breadcrumbKey: 'seoLanding.security.breadcrumb',
    titleKey: 'seoLanding.security.title',
    leadKey: 'seoLanding.security.lead',
    bodyKeys: [
      'seoLanding.security.p1',
      'seoLanding.security.p2',
      'seoLanding.security.p3',
      'seoLanding.security.p4',
      'seoLanding.security.p5',
    ],
    faqHeadingKey: 'seoLanding.security.faqHeading',
    ctaKey: 'seoLanding.esignature.cta',
    pricingHref: '#pricing',
    pricingLabelKey: 'seoLanding.esignature.pricingLink',
    crossLinks: [
      { hrefLogical: '/trust', labelKey: 'footer.linkTrust' },
      { hrefLogical: '/why-primedocumentsign', labelKey: 'footer.linkWhyPrimeDocuSign' },
      { hrefLogical: '/esignature-legal', labelKey: 'footer.linkEsignLegal' },
      { hrefLogical: '/about', labelKey: 'nav.aboutUs' },
    ],
  },
  '/esignature-legal': {
    htmlId: 'esignature-legal-top',
    breadcrumbKey: 'seoLanding.esignLegal.breadcrumb',
    titleKey: 'seoLanding.esignLegal.title',
    leadKey: 'seoLanding.esignLegal.lead',
    bodyKeys: ['seoLanding.esignLegal.p1', 'seoLanding.esignLegal.p2', 'seoLanding.esignLegal.p3'],
    bodyKeysPreline: ['seoLanding.esignLegal.p2'],
    faqHeadingKey: 'seoLanding.esignLegal.faqHeading',
    ctaKey: 'seoLanding.esignature.cta',
    pricingHref: '#pricing',
    pricingLabelKey: 'seoLanding.esignature.pricingLink',
    crossLinks: [
      { hrefLogical: '/trust', labelKey: 'footer.linkTrust' },
      { hrefLogical: '/what-is-esignature', labelKey: 'footer.linkWhatIsEsign' },
      { hrefLogical: '/security', labelKey: 'footer.linkSecurity' },
      { hrefLogical: '/about', labelKey: 'nav.aboutUs' },
    ],
  },
  '/why-primedocumentsign': {
    htmlId: 'why-prime-documentsign-top',
    breadcrumbKey: 'seoLanding.whyPrime.breadcrumb',
    titleKey: 'seoLanding.whyPrime.title',
    leadKey: 'seoLanding.whyPrime.lead',
    bodyKeys: [
      'seoLanding.whyPrime.p1',
      'seoLanding.whyPrime.p2',
      'seoLanding.whyPrime.p3',
      'seoLanding.whyPrime.p4',
      'seoLanding.whyPrime.p5',
    ],
    bodyKeysPreline: ['seoLanding.whyPrime.p3'],
    faqHeadingKey: 'seoLanding.whyPrime.faqHeading',
    ctaKey: 'seoLanding.esignature.cta',
    pricingHref: '#pricing',
    pricingLabelKey: 'seoLanding.esignature.pricingLink',
    crossLinks: [
      { hrefLogical: '/trust', labelKey: 'footer.linkTrust' },
      { hrefLogical: '/product', labelKey: 'footer.linkProduct' },
      { hrefLogical: '/security', labelKey: 'footer.linkSecurity' },
      { hrefLogical: '/esignature-legal', labelKey: 'footer.linkEsignLegal' },
      { hrefLogical: '/esignature', labelKey: 'footer.linkEsignature' },
    ],
  },
  '/about': {
    htmlId: 'about-standalone-top',
    breadcrumbKey: 'seoLanding.aboutTrust.breadcrumb',
    titleKey: 'seoLanding.aboutTrust.title',
    leadKey: 'seoLanding.aboutTrust.lead',
    bodyKeys: [
      'seoLanding.aboutTrust.p1',
      'seoLanding.aboutTrust.p2',
      'seoLanding.aboutTrust.p3',
      'seoLanding.aboutTrust.p4',
      'seoLanding.aboutTrust.p5',
      'seoLanding.aboutTrust.p6',
    ],
    faqHeadingKey: 'seoLanding.aboutTrust.faqHeading',
    ctaKey: 'seoLanding.esignature.cta',
    pricingHref: '#pricing',
    pricingLabelKey: 'seoLanding.esignature.pricingLink',
    crossLinks: [
      { hrefLogical: '/trust', labelKey: 'footer.linkTrust' },
      { hrefLogical: '/product', labelKey: 'footer.linkProduct' },
      { hrefLogical: '/security', labelKey: 'footer.linkSecurity' },
      { hrefLogical: '/esignature-legal', labelKey: 'footer.linkEsignLegal' },
    ],
  },
  '/trust': {
    htmlId: 'trust-master-top',
    breadcrumbKey: 'seoLanding.trustMaster.breadcrumb',
    titleKey: 'seoLanding.trustMaster.title',
    leadKey: 'seoLanding.trustMaster.lead',
    sectionBlocks: [
      {
        titleKey: 'seoLanding.trustMaster.secSecureTitle',
        bodyKeys: ['seoLanding.trustMaster.secSecureP1', 'seoLanding.trustMaster.secSecureP2'],
      },
      {
        titleKey: 'seoLanding.trustMaster.secEncryptTitle',
        bodyKeys: ['seoLanding.trustMaster.secEncryptP1', 'seoLanding.trustMaster.secEncryptP2'],
      },
      {
        titleKey: 'seoLanding.trustMaster.secAuditTitle',
        bodyKeys: ['seoLanding.trustMaster.secAuditP1', 'seoLanding.trustMaster.secAuditP2'],
      },
      {
        titleKey: 'seoLanding.trustMaster.secLegalTitle',
        bodyKeys: ['seoLanding.trustMaster.secLegalP1', 'seoLanding.trustMaster.secLegalP2'],
      },
      {
        titleKey: 'seoLanding.trustMaster.secPrivacyTitle',
        bodyKeys: ['seoLanding.trustMaster.secPrivacyP1', 'seoLanding.trustMaster.secPrivacyP2'],
      },
      {
        titleKey: 'seoLanding.trustMaster.secCompanyTitle',
        bodyKeys: ['seoLanding.trustMaster.secCompanyP1', 'seoLanding.trustMaster.secCompanyP2'],
      },
      {
        titleKey: 'seoLanding.trustMaster.secListingTitle',
        bodyKeys: ['seoLanding.trustMaster.secListingP1'],
      },
    ],
    faqHeadingKey: 'seoLanding.trustMaster.faqHeading',
    ctaKey: 'seoLanding.esignature.cta',
    pricingHref: '#pricing',
    pricingLabelKey: 'seoLanding.esignature.pricingLink',
    crossLinks: [
      { hrefLogical: '/product', labelKey: 'footer.linkProduct' },
      { hrefLogical: '/security', labelKey: 'footer.linkSecurity' },
      { hrefLogical: '/esignature-legal', labelKey: 'footer.linkEsignLegal' },
      { hrefLogical: '/reviews', labelKey: 'footer.linkReviews' },
    ],
  },
  '/product': {
    htmlId: 'product-reference-top',
    breadcrumbKey: 'seoLanding.productRef.breadcrumb',
    titleKey: 'seoLanding.productRef.title',
    leadKey: 'seoLanding.productRef.lead',
    sectionBlocks: [
      {
        titleKey: 'seoLanding.productRef.secWhatTitle',
        bodyKeys: ['seoLanding.productRef.secWhatP1', 'seoLanding.productRef.secWhatP2'],
      },
      {
        titleKey: 'seoLanding.productRef.secFeaturesTitle',
        bodyKeys: ['seoLanding.productRef.secFeaturesP1', 'seoLanding.productRef.secFeaturesP2'],
      },
      {
        titleKey: 'seoLanding.productRef.secComplianceTitle',
        bodyKeys: ['seoLanding.productRef.secComplianceP1', 'seoLanding.productRef.secComplianceP2'],
      },
      {
        titleKey: 'seoLanding.productRef.secPricingTitle',
        bodyKeys: ['seoLanding.productRef.secPricingP1', 'seoLanding.productRef.secPricingP2'],
      },
      {
        titleKey: 'seoLanding.productRef.secUseCasesTitle',
        bodyKeys: ['seoLanding.productRef.secUseCasesP1', 'seoLanding.productRef.secUseCasesP2'],
      },
    ],
    faqHeadingKey: 'seoLanding.productRef.faqHeading',
    ctaKey: 'seoLanding.esignature.cta',
    pricingHref: '#pricing',
    pricingLabelKey: 'seoLanding.esignature.pricingLink',
    crossLinks: [
      { hrefLogical: '/trust', labelKey: 'footer.linkTrust' },
      { hrefLogical: '/what-is-esignature', labelKey: 'footer.linkWhatIsEsign' },
      { hrefLogical: '/docusign-alternative', labelKey: 'footer.docusignAlt' },
      { hrefLogical: '/esignature', labelKey: 'footer.linkEsignature' },
    ],
  },
  '/alternatives': {
    htmlId: 'alternatives-hub-top',
    breadcrumbKey: 'seoLanding.altHub.breadcrumb',
    titleKey: 'seoLanding.altHub.title',
    leadKey: 'seoLanding.altHub.lead',
    bodyKeys: ['seoLanding.altHub.p1', 'seoLanding.altHub.p2', 'seoLanding.altHub.p3'],
    faqHeadingKey: 'seoLanding.altHub.faqHeading',
    ctaKey: 'seoLanding.esignature.cta',
    pricingHref: '#pricing',
    pricingLabelKey: 'seoLanding.esignature.pricingLink',
    crossLinks: [
      { hrefLogical: '/docusign-alternative', labelKey: 'footer.docusignAlt' },
      { hrefLogical: '/docusign-vs-prime-document-sign', labelKey: 'footer.linkDocuSignVs' },
      { hrefLogical: '/free-esignature', labelKey: 'footer.linkFreeEsign' },
      { hrefLogical: '/product', labelKey: 'footer.linkProduct' },
    ],
  },
  '/what-is-esignature': {
    htmlId: 'what-is-esignature-top',
    breadcrumbKey: 'seoLanding.whatEsign.breadcrumb',
    titleKey: 'seoLanding.whatEsign.title',
    leadKey: 'seoLanding.whatEsign.lead',
    sectionBlocks: [
      {
        titleKey: 'seoLanding.whatEsign.secDefTitle',
        bodyKeys: ['seoLanding.whatEsign.secDefP1'],
      },
      {
        titleKey: 'seoLanding.whatEsign.secHowTitle',
        bodyKeys: ['seoLanding.whatEsign.secHowP1', 'seoLanding.whatEsign.secHowP2'],
      },
      {
        titleKey: 'seoLanding.whatEsign.secLegalTitle',
        bodyKeys: ['seoLanding.whatEsign.secLegalP1', 'seoLanding.whatEsign.secLegalP2'],
      },
      {
        titleKey: 'seoLanding.whatEsign.secBenefitsTitle',
        bodyKeys: ['seoLanding.whatEsign.secBenefitsP1', 'seoLanding.whatEsign.secBenefitsP2'],
      },
    ],
    faqHeadingKey: 'seoLanding.whatEsign.faqHeading',
    ctaKey: 'seoLanding.esignature.cta',
    pricingHref: '#pricing',
    pricingLabelKey: 'seoLanding.esignature.pricingLink',
    crossLinks: [
      { hrefLogical: '/product', labelKey: 'footer.linkProduct' },
      { hrefLogical: '/trust', labelKey: 'footer.linkTrust' },
      { hrefLogical: '/esignature-legal', labelKey: 'footer.linkEsignLegal' },
      { hrefLogical: '/esignature', labelKey: 'footer.linkEsignature' },
    ],
  },
  '/reviews': {
    htmlId: 'reviews-top',
    breadcrumbKey: 'seoLanding.reviewsPage.breadcrumb',
    titleKey: 'seoLanding.reviewsPage.title',
    leadKey: 'seoLanding.reviewsPage.lead',
    bodyKeys: [
      'seoLanding.reviewsPage.p1',
      'seoLanding.reviewsPage.p2',
      'seoLanding.reviewsPage.p3',
      'seoLanding.reviewsPage.p4',
    ],
    faqHeadingKey: 'seoLanding.reviewsPage.faqHeading',
    ctaKey: 'seoLanding.esignature.cta',
    pricingHref: '#pricing',
    pricingLabelKey: 'seoLanding.esignature.pricingLink',
    crossLinks: [
      { hrefLogical: '/trust', labelKey: 'footer.linkTrust' },
      { hrefLogical: '/about', labelKey: 'nav.aboutUs' },
      { hrefLogical: '/docusign-alternative', labelKey: 'footer.docusignAlt' },
      { hrefLogical: '/product', labelKey: 'footer.linkProduct' },
    ],
  },
  '/terms': {
    htmlId: 'terms-of-service-top',
    breadcrumbKey: 'footer.terms',
    legalPage: 'terms',
    updatedLabelKey: 'legal.termsLastUpdated',
    ctaKey: 'seoLanding.esignature.cta',
    pricingHref: '#pricing',
    pricingLabelKey: 'seoLanding.esignature.pricingLink',
  },
  '/privacy': {
    htmlId: 'privacy-policy-top',
    breadcrumbKey: 'footer.privacy',
    legalPage: 'privacy',
    updatedLabelKey: 'legal.privacyLastUpdated',
    ctaKey: 'seoLanding.esignature.cta',
    pricingHref: '#pricing',
    pricingLabelKey: 'seoLanding.esignature.pricingLink',
  },
}

const ESIGNATURE_HUB_FAQ = [
  { qKey: 'faq.seoWhatIsESignatureQ', aKey: 'faq.seoWhatIsESignatureA' },
  { qKey: 'faq.seoLegallyBindingQ', aKey: 'faq.seoLegallyBindingA' },
  { qKey: 'faq.seoSecureQ', aKey: 'faq.seoSecureA' },
  { qKey: 'faq.seoFirebaseSecurityQ', aKey: 'faq.seoFirebaseSecurityA', multiParagraph: true },
  { qKey: 'faq.seoGoogleBusinessProfileQ', aKey: 'faq.seoGoogleBusinessProfileA', multiParagraph: true },
  { qKey: 'faq.seoWhatIsPrimeQ', aKey: 'faq.seoWhatIsPrimeA' },
  { qKey: 'faq.seoAuditTrailQ', aKey: 'faq.seoAuditTrailA' },
  { qKey: 'faq.aiWhatIsESignSoftwareForQ', aKey: 'faq.aiWhatIsESignSoftwareForA', multiParagraph: true },
  { qKey: 'faq.aiHowMuchDoesESignCostQ', aKey: 'faq.aiHowMuchDoesESignCostA', multiParagraph: true },
  { qKey: 'faq.aiHowChooseESignProviderQ', aKey: 'faq.aiHowChooseESignProviderA', multiParagraph: true },
]

function freeEsignFaqs(tierPricing) {
  const ot = tierPricing.oneTimePrice
  return [
    { qKey: 'faq.seoFreeFirstDocQ', aKey: 'faq.seoFreeFirstDocA' },
    { qKey: 'faq.aiFreeESignToolQ', aKey: 'faq.aiFreeESignToolA', multiParagraph: true, params: { oneTimePrice: ot } },
    { qKey: 'faq.aiSignPdfOnlineFreeQ', aKey: 'faq.aiSignPdfOnlineFreeA', multiParagraph: true },
    { qKey: 'faq.recipientAccountQ', aKey: 'faq.recipientAccountA' },
    { qKey: 'faq.confirmationEmailQ', aKey: 'faq.confirmationEmailA' },
    { qKey: 'faq.aiAffordableSignOnlineQ', aKey: 'faq.aiAffordableSignOnlineA', multiParagraph: true },
  ]
}

/** Core template workflow — reused on every /templates/* subpage (after page-specific extras). */
const TEMPLATES_PAGE_FAQ = [
  { qKey: 'seoLanding.templates.faqWhyQ', aKey: 'seoLanding.templates.faqWhyA', multiParagraph: true },
  { qKey: 'seoLanding.templates.faqStarterQ', aKey: 'seoLanding.templates.faqStarterA', multiParagraph: true },
  { qKey: 'faq.reuseTemplatesQ', aKey: 'faq.reuseTemplatesA' },
  { qKey: 'faq.saveTemplateQ', aKey: 'faq.saveTemplateA' },
  { qKey: 'faq.templateLimitsQ', aKey: 'faq.templateLimitsA' },
  { qKey: 'faq.loadTemplateQ', aKey: 'faq.loadTemplateA' },
]

/** Extra items on /templates hub only — richer FAQPage + visible accordion. */
const TEMPLATES_HUB_ONLY_FAQ = [
  { qKey: 'seoLanding.templates.faqSignedPdfQ', aKey: 'seoLanding.templates.faqSignedPdfA', multiParagraph: true },
  { qKey: 'seoLanding.templates.faqMultiSignerQ', aKey: 'seoLanding.templates.faqMultiSignerA', multiParagraph: true },
  { qKey: 'seoLanding.templates.faqNotLegalAdviceQ', aKey: 'seoLanding.templates.faqNotLegalAdviceA', multiParagraph: true },
  { qKey: 'seoLanding.templates.faqUpdateTemplateQ', aKey: 'seoLanding.templates.faqUpdateTemplateA', multiParagraph: true },
]

const TEMPLATES_HUB_FAQ = [...TEMPLATES_PAGE_FAQ, ...TEMPLATES_HUB_ONLY_FAQ]

const TPL_NDA_EXTRA_FAQ = [
  { qKey: 'seoLanding.tplNda.faqMutualQ', aKey: 'seoLanding.tplNda.faqMutualA', multiParagraph: true },
  { qKey: 'seoLanding.tplNda.faqTradeSecretsQ', aKey: 'seoLanding.tplNda.faqTradeSecretsA', multiParagraph: true },
]
const TPL_CONTRACT_EXTRA_FAQ = [
  { qKey: 'seoLanding.tplContract.faqMsaSowQ', aKey: 'seoLanding.tplContract.faqMsaSowA', multiParagraph: true },
  { qKey: 'seoLanding.tplContract.faqPaymentScheduleQ', aKey: 'seoLanding.tplContract.faqPaymentScheduleA', multiParagraph: true },
]
const TPL_FREELANCE_EXTRA_FAQ = [
  { qKey: 'seoLanding.tplFreelance.faqIpDeliverablesQ', aKey: 'seoLanding.tplFreelance.faqIpDeliverablesA', multiParagraph: true },
  { qKey: 'seoLanding.tplFreelance.faqRetainerQ', aKey: 'seoLanding.tplFreelance.faqRetainerA', multiParagraph: true },
]
const TPL_LEASE_EXTRA_FAQ = [
  { qKey: 'seoLanding.tplLease.faqCosignerQ', aKey: 'seoLanding.tplLease.faqCosignerA', multiParagraph: true },
  { qKey: 'seoLanding.tplLease.faqCommercialVsResidentialQ', aKey: 'seoLanding.tplLease.faqCommercialVsResidentialA', multiParagraph: true },
]
const TPL_SERVICE_EXTRA_FAQ = [
  { qKey: 'seoLanding.tplService.faqSlaQ', aKey: 'seoLanding.tplService.faqSlaA', multiParagraph: true },
  { qKey: 'seoLanding.tplService.faqChangeOrderQ', aKey: 'seoLanding.tplService.faqChangeOrderA', multiParagraph: true },
]
const TPL_CONTRACTOR_EXTRA_FAQ = [
  { qKey: 'seoLanding.tplContractor.faqVsEmployeeQ', aKey: 'seoLanding.tplContractor.faqVsEmployeeA', multiParagraph: true },
  { qKey: 'seoLanding.tplContractor.faqInsuranceQ', aKey: 'seoLanding.tplContractor.faqInsuranceA', multiParagraph: true },
]
const TPL_PO_EXTRA_FAQ = [
  { qKey: 'seoLanding.tplPo.faqVsContractQ', aKey: 'seoLanding.tplPo.faqVsContractA', multiParagraph: true },
  { qKey: 'seoLanding.tplPo.faqApprovalQ', aKey: 'seoLanding.tplPo.faqApprovalA', multiParagraph: true },
]
const TPL_PHOTO_EXTRA_FAQ = [
  { qKey: 'seoLanding.tplPhotoRelease.faqTalentQ', aKey: 'seoLanding.tplPhotoRelease.faqTalentA', multiParagraph: true },
  { qKey: 'seoLanding.tplPhotoRelease.faqCommercialUseQ', aKey: 'seoLanding.tplPhotoRelease.faqCommercialUseA', multiParagraph: true },
]

function templateSubpageFaqWithCore(extra) {
  return [...extra, ...TEMPLATES_PAGE_FAQ]
}

const ESIGN_RE_EXTRA_FAQ = [
  { qKey: 'seoLanding.esignRe.faqDisclosuresQ', aKey: 'seoLanding.esignRe.faqDisclosuresA', multiParagraph: true },
  { qKey: 'seoLanding.esignRe.faqRemoteClosingQ', aKey: 'seoLanding.esignRe.faqRemoteClosingA', multiParagraph: true },
]
const ESIGN_ACCT_EXTRA_FAQ = [
  { qKey: 'seoLanding.esignAcct.faqEngagementLetterQ', aKey: 'seoLanding.esignAcct.faqEngagementLetterA', multiParagraph: true },
  { qKey: 'seoLanding.esignAcct.faqTaxAuthQ', aKey: 'seoLanding.esignAcct.faqTaxAuthA', multiParagraph: true },
]
const ESIGN_HR_EXTRA_FAQ = [
  { qKey: 'seoLanding.esignHr.faqOfferLetterQ', aKey: 'seoLanding.esignHr.faqOfferLetterA', multiParagraph: true },
  { qKey: 'seoLanding.esignHr.faqPolicyAckQ', aKey: 'seoLanding.esignHr.faqPolicyAckA', multiParagraph: true },
]
const ESIGN_AGENCY_EXTRA_FAQ = [
  { qKey: 'seoLanding.esignAgency.faqSowQ', aKey: 'seoLanding.esignAgency.faqSowA', multiParagraph: true },
  { qKey: 'seoLanding.esignAgency.faqChangeOrderQ', aKey: 'seoLanding.esignAgency.faqChangeOrderA', multiParagraph: true },
]

/** “Why us” page — distinct from generic /esignature hub FAQ schema. */
const WHY_PRIME_FAQ = [
  { qKey: 'faq.seoWhatIsPrimeQ', aKey: 'faq.seoWhatIsPrimeA', multiParagraph: true },
  { qKey: 'faq.aiBestSmallBusinessESignQ', aKey: 'faq.aiBestSmallBusinessESignA', multiParagraph: true },
  { qKey: 'faq.aiFreelancerContractSigningQ', aKey: 'faq.aiFreelancerContractSigningA', multiParagraph: true },
  { qKey: 'faq.primeFeaturesForTeamsQ', aKey: 'faq.primeFeaturesForTeamsA', multiParagraph: true },
  { qKey: 'faq.seoFreeFirstDocQ', aKey: 'faq.seoFreeFirstDocA' },
  { qKey: 'faq.aiHowMuchDoesESignCostQ', aKey: 'faq.aiHowMuchDoesESignCostA', multiParagraph: true },
  { qKey: 'faq.seoSecureQ', aKey: 'faq.seoSecureA' },
  { qKey: 'faq.seoDocuSignAlternativeQ', aKey: 'faq.seoDocuSignAlternativeA', multiParagraph: true },
  { qKey: 'faq.primeTrustWhoWeAreQ', aKey: 'faq.primeTrustWhoWeAreA', multiParagraph: true },
  { qKey: 'faq.seoAuditTrailQ', aKey: 'faq.seoAuditTrailA' },
]

const AU_PAGE_FAQ = [
  { qKey: 'seoLanding.esignAu.faqLegalQ', aKey: 'seoLanding.esignAu.faqLegalA', multiParagraph: true },
  { qKey: 'seoLanding.esignAu.faqEtaQ', aKey: 'seoLanding.esignAu.faqEtaA', multiParagraph: true },
  { qKey: 'faq.seoLegallyBindingQ', aKey: 'faq.seoLegallyBindingA' },
  { qKey: 'faq.seoSecureQ', aKey: 'faq.seoSecureA' },
  { qKey: 'faq.seoFirebaseSecurityQ', aKey: 'faq.seoFirebaseSecurityA', multiParagraph: true },
  { qKey: 'seoLanding.esignAu.faqBusinessQ', aKey: 'seoLanding.esignAu.faqBusinessA', multiParagraph: true },
  { qKey: 'faq.seoFreeFirstDocQ', aKey: 'faq.seoFreeFirstDocA' },
]

/** `/docusign-vs-prime-document-sign` — conversion-focused FAQs + JSON-LD */
const DOCUSIGN_VS_PAGE_FAQ = [
  { qKey: 'faq.docuVs.alternativeQ', aKey: 'faq.docuVs.alternativeA', multiParagraph: true },
  { qKey: 'faq.docuVs.pricingCompareQ', aKey: 'faq.docuVs.pricingCompareA', multiParagraph: true },
  { qKey: 'faq.docuVs.freeSendQ', aKey: 'faq.docuVs.freeSendA', multiParagraph: true },
  { qKey: 'faq.seoMigrateFromDocuSignQ', aKey: 'faq.seoMigrateFromDocuSignA' },
  { qKey: 'faq.seoLegallyBindingQ', aKey: 'faq.seoLegallyBindingA' },
  { qKey: 'faq.seoSecureQ', aKey: 'faq.seoSecureA' },
  { qKey: 'faq.seoFirebaseSecurityQ', aKey: 'faq.seoFirebaseSecurityA', multiParagraph: true },
]

/** Trust & security — encryption, access control, audit context */
const SECURITY_TRUST_FAQ = [
  { qKey: 'faq.seoSecureQ', aKey: 'faq.seoSecureA' },
  { qKey: 'faq.seoFirebaseSecurityQ', aKey: 'faq.seoFirebaseSecurityA', multiParagraph: true },
  { qKey: 'faq.dataSecureQ', aKey: 'faq.dataSecureA' },
  { qKey: 'faq.seoAuditTrailQ', aKey: 'faq.seoAuditTrailA' },
  { qKey: 'faq.primeTrustWhoWeAreQ', aKey: 'faq.primeTrustWhoWeAreA', multiParagraph: true },
  { qKey: 'faq.seoLegallyBindingQ', aKey: 'faq.seoLegallyBindingA' },
  { qKey: 'faq.primeComplianceEsignEidasQ', aKey: 'faq.primeComplianceEsignEidasA', multiParagraph: true },
]

/** Legal validity, compliance framing, audit trail */
const ESIGN_LEGAL_FAQ = [
  { qKey: 'faq.seoLegallyBindingQ', aKey: 'faq.seoLegallyBindingA' },
  { qKey: 'faq.primeComplianceEsignEidasQ', aKey: 'faq.primeComplianceEsignEidasA', multiParagraph: true },
  { qKey: 'faq.seoAuditTrailQ', aKey: 'faq.seoAuditTrailA' },
  { qKey: 'faq.seoWhatIsESignatureQ', aKey: 'faq.seoWhatIsESignatureA' },
  { qKey: 'faq.seoSecureQ', aKey: 'faq.seoSecureA' },
  { qKey: 'faq.seoMigrateFromDocuSignQ', aKey: 'faq.seoMigrateFromDocuSignA' },
]

/** Company trust narrative */
const ABOUT_TRUST_FAQ = [
  { qKey: 'faq.seoWhatIsPrimeQ', aKey: 'faq.seoWhatIsPrimeA' },
  { qKey: 'faq.primeTrustWhoWeAreQ', aKey: 'faq.primeTrustWhoWeAreA', multiParagraph: true },
  { qKey: 'faq.seoGoogleBusinessProfileQ', aKey: 'faq.seoGoogleBusinessProfileA', multiParagraph: true },
  { qKey: 'faq.primeSocialProofQ', aKey: 'faq.primeSocialProofA', multiParagraph: true },
  { qKey: 'faq.seoSecureQ', aKey: 'faq.seoSecureA' },
  { qKey: 'faq.seoFirebaseSecurityQ', aKey: 'faq.seoFirebaseSecurityA', multiParagraph: true },
  { qKey: 'faq.seoLegallyBindingQ', aKey: 'faq.seoLegallyBindingA' },
  { qKey: 'faq.seoWhoUsesPrimeQ', aKey: 'faq.seoWhoUsesPrimeA' },
  { qKey: 'faq.primeComplianceEsignEidasQ', aKey: 'faq.primeComplianceEsignEidasA', multiParagraph: true },
]

/** Master trust hub — security, encryption, audit, legal, privacy, company */
const TRUST_MASTER_FAQ = [
  { qKey: 'faq.seoSecureQ', aKey: 'faq.seoSecureA' },
  { qKey: 'faq.seoFirebaseSecurityQ', aKey: 'faq.seoFirebaseSecurityA', multiParagraph: true },
  { qKey: 'faq.seoAuditTrailQ', aKey: 'faq.seoAuditTrailA' },
  { qKey: 'faq.seoLegallyBindingQ', aKey: 'faq.seoLegallyBindingA' },
  { qKey: 'faq.primeComplianceEsignEidasQ', aKey: 'faq.primeComplianceEsignEidasA', multiParagraph: true },
  { qKey: 'faq.dataSecureQ', aKey: 'faq.dataSecureA' },
  { qKey: 'faq.primeTrustWhoWeAreQ', aKey: 'faq.primeTrustWhoWeAreA', multiParagraph: true },
  { qKey: 'faq.seoGoogleBusinessProfileQ', aKey: 'faq.seoGoogleBusinessProfileA', multiParagraph: true },
]

const PRODUCT_REFERENCE_FAQ = [
  { qKey: 'faq.seoWhatIsPrimeQ', aKey: 'faq.seoWhatIsPrimeA' },
  { qKey: 'faq.seoFreeFirstDocQ', aKey: 'faq.seoFreeFirstDocA' },
  { qKey: 'faq.aiHowMuchDoesESignCostQ', aKey: 'faq.aiHowMuchDoesESignCostA', multiParagraph: true },
  { qKey: 'faq.seoSecureQ', aKey: 'faq.seoSecureA' },
  { qKey: 'faq.seoAuditTrailQ', aKey: 'faq.seoAuditTrailA' },
  { qKey: 'faq.seoWhoUsesPrimeQ', aKey: 'faq.seoWhoUsesPrimeA' },
]

const ALTERNATIVES_HUB_FAQ = [
  { qKey: 'faq.aiWhatIsDocuSignAlternativeQ', aKey: 'faq.aiWhatIsDocuSignAlternativeA', multiParagraph: true },
  { qKey: 'faq.docuVs.alternativeQ', aKey: 'faq.docuVs.alternativeA', multiParagraph: true },
  { qKey: 'faq.seoMigrateFromDocuSignQ', aKey: 'faq.seoMigrateFromDocuSignA' },
  { qKey: 'faq.aiBestSmallBusinessESignQ', aKey: 'faq.aiBestSmallBusinessESignA', multiParagraph: true },
  { qKey: 'faq.seoLegallyBindingQ', aKey: 'faq.seoLegallyBindingA' },
]

const WHAT_IS_ESIGN_FAQ = [
  { qKey: 'faq.seoWhatIsESignatureQ', aKey: 'faq.seoWhatIsESignatureA' },
  { qKey: 'faq.seoLegallyBindingQ', aKey: 'faq.seoLegallyBindingA' },
  { qKey: 'faq.primeComplianceEsignEidasQ', aKey: 'faq.primeComplianceEsignEidasA', multiParagraph: true },
  { qKey: 'faq.seoAuditTrailQ', aKey: 'faq.seoAuditTrailA' },
  { qKey: 'faq.seoSecureQ', aKey: 'faq.seoSecureA' },
  { qKey: 'faq.aiHowChooseESignProviderQ', aKey: 'faq.aiHowChooseESignProviderA', multiParagraph: true },
]

const REVIEWS_PAGE_FAQ = [
  { qKey: 'faq.aiThirdPartyDirectoryReviewsQ', aKey: 'faq.aiThirdPartyDirectoryReviewsA', multiParagraph: true },
  { qKey: 'faq.seoGoogleBusinessProfileQ', aKey: 'faq.seoGoogleBusinessProfileA', multiParagraph: true },
  { qKey: 'faq.primeSocialProofQ', aKey: 'faq.primeSocialProofA', multiParagraph: true },
  { qKey: 'faq.primeCustomerStory1Q', aKey: 'faq.primeCustomerStory1A', multiParagraph: true },
  { qKey: 'faq.primeCustomerStory2Q', aKey: 'faq.primeCustomerStory2A', multiParagraph: true },
  { qKey: 'faq.seoSecureQ', aKey: 'faq.seoSecureA' },
  { qKey: 'faq.seoWhatIsPrimeQ', aKey: 'faq.seoWhatIsPrimeA' },
]

/**
 * FAQs shown in the top article + FAQPage JSON-LD for that URL.
 * @param {object} docuSignAltFaqItems - from App (DOCUSIGN_ALT_FAQ_ITEMS)
 */
export function getSeoArticleFaqItems(pathname, tierPricing, docuSignAltFaqItems) {
  const p = normalizePathname(pathname)
  switch (p) {
    case '/esignature':
      return ESIGNATURE_HUB_FAQ
    case '/esignature/real-estate':
      return [...ESIGN_RE_EXTRA_FAQ, ...ESIGNATURE_HUB_FAQ]
    case '/esignature/accountants':
      return [...ESIGN_ACCT_EXTRA_FAQ, ...ESIGNATURE_HUB_FAQ]
    case '/esignature/hr':
      return [...ESIGN_HR_EXTRA_FAQ, ...ESIGNATURE_HUB_FAQ]
    case '/esignature/agencies':
      return [...ESIGN_AGENCY_EXTRA_FAQ, ...ESIGNATURE_HUB_FAQ]
    case '/docusign-vs-prime-document-sign':
      return DOCUSIGN_VS_PAGE_FAQ
    case '/docusign-alternative':
      return docuSignAltFaqItems
    case '/free-esignature':
      return freeEsignFaqs(tierPricing)
    case '/templates':
      return TEMPLATES_HUB_FAQ
    case '/templates/nda':
      return templateSubpageFaqWithCore(TPL_NDA_EXTRA_FAQ)
    case '/templates/contract':
      return templateSubpageFaqWithCore(TPL_CONTRACT_EXTRA_FAQ)
    case '/templates/freelance-agreement':
      return templateSubpageFaqWithCore(TPL_FREELANCE_EXTRA_FAQ)
    case '/templates/lease-agreement':
      return templateSubpageFaqWithCore(TPL_LEASE_EXTRA_FAQ)
    case '/templates/service-agreement':
      return templateSubpageFaqWithCore(TPL_SERVICE_EXTRA_FAQ)
    case '/templates/independent-contractor':
      return templateSubpageFaqWithCore(TPL_CONTRACTOR_EXTRA_FAQ)
    case '/templates/purchase-order':
      return templateSubpageFaqWithCore(TPL_PO_EXTRA_FAQ)
    case '/templates/photo-release':
      return templateSubpageFaqWithCore(TPL_PHOTO_EXTRA_FAQ)
    case '/esignature-australia':
      return AU_PAGE_FAQ
    case '/security':
      return SECURITY_TRUST_FAQ
    case '/esignature-legal':
      return ESIGN_LEGAL_FAQ
    case '/why-primedocumentsign':
      return WHY_PRIME_FAQ
    case '/about':
      return ABOUT_TRUST_FAQ
    case '/trust':
      return TRUST_MASTER_FAQ
    case '/product':
      return PRODUCT_REFERENCE_FAQ
    case '/alternatives':
      return ALTERNATIVES_HUB_FAQ
    case '/what-is-esignature':
      return WHAT_IS_ESIGN_FAQ
    case '/reviews':
      return REVIEWS_PAGE_FAQ
    case '/terms':
    case '/privacy':
      return []
    default:
      return []
  }
}

export function getMetaTitleKeyForPath(pathname) {
  const p = normalizePathname(pathname)
  const map = {
    '/esignature': 'meta.titleEsignature',
    '/esignature/real-estate': 'meta.titleEsignRealEstate',
    '/esignature/accountants': 'meta.titleEsignAccountants',
    '/esignature/hr': 'meta.titleEsignHr',
    '/esignature/agencies': 'meta.titleEsignAgencies',
    '/docusign-alternative': 'meta.titleDocusignAlt',
    '/docusign-vs-prime-document-sign': 'meta.titleDocuVsPrime',
    '/free-esignature': 'meta.titleFreeEsign',
    '/templates': 'meta.titleTemplates',
    '/templates/nda': 'meta.titleTplNda',
    '/templates/contract': 'meta.titleTplContract',
    '/templates/freelance-agreement': 'meta.titleTplFreelance',
    '/templates/lease-agreement': 'meta.titleTplLease',
    '/templates/service-agreement': 'meta.titleTplService',
    '/templates/independent-contractor': 'meta.titleTplContractor',
    '/templates/purchase-order': 'meta.titleTplPo',
    '/templates/photo-release': 'meta.titleTplPhotoRelease',
    '/esignature-australia': 'meta.titleEsignAu',
    '/why-primedocumentsign': 'meta.titleWhyPrime',
    '/security': 'meta.titleSecurity',
    '/esignature-legal': 'meta.titleEsignLegal',
    '/about': 'meta.titleAboutPage',
    '/terms': 'meta.titleTermsOfService',
    '/privacy': 'meta.titlePrivacyPolicy',
    '/pricing': 'meta.titlePricing',
    '/trust': 'meta.titleTrust',
    '/product': 'meta.titleProduct',
    '/alternatives': 'meta.titleAlternatives',
    '/what-is-esignature': 'meta.titleWhatIsEsignature',
    '/reviews': 'meta.titleReviews',
  }
  return map[p] || null
}

export function getMetaDescKeyForPath(pathname) {
  const p = normalizePathname(pathname)
  const map = {
    '/esignature': 'meta.descEsignature',
    '/esignature/real-estate': 'meta.descEsignRealEstate',
    '/esignature/accountants': 'meta.descEsignAccountants',
    '/esignature/hr': 'meta.descEsignHr',
    '/esignature/agencies': 'meta.descEsignAgencies',
    '/docusign-alternative': 'meta.descDocusignAlt',
    '/docusign-vs-prime-document-sign': 'meta.descDocuVsPrime',
    '/free-esignature': 'meta.descFreeEsign',
    '/templates': 'meta.descTemplates',
    '/templates/nda': 'meta.descTplNda',
    '/templates/contract': 'meta.descTplContract',
    '/templates/freelance-agreement': 'meta.descTplFreelance',
    '/templates/lease-agreement': 'meta.descTplLease',
    '/templates/service-agreement': 'meta.descTplService',
    '/templates/independent-contractor': 'meta.descTplContractor',
    '/templates/purchase-order': 'meta.descTplPo',
    '/templates/photo-release': 'meta.descTplPhotoRelease',
    '/esignature-australia': 'meta.descEsignAu',
    '/why-primedocumentsign': 'meta.descWhyPrime',
    '/security': 'meta.descSecurity',
    '/esignature-legal': 'meta.descEsignLegal',
    '/about': 'meta.descAboutPage',
    '/terms': 'meta.descTermsOfService',
    '/privacy': 'meta.descPrivacyPolicy',
    '/pricing': 'meta.descPricing',
    '/trust': 'meta.descTrust',
    '/product': 'meta.descProduct',
    '/alternatives': 'meta.descAlternatives',
    '/what-is-esignature': 'meta.descWhatIsEsignature',
    '/reviews': 'meta.descReviews',
  }
  return map[p] || null
}
